<template>
  <div v-if="selected_journey?.phases" class="overflow-hidden h-full">
    <div class="bg-red-error" />
    <div class="bg-green-success" />
    <div class="bg-status-warning-yellow" />

    <div class="bg-red-error/10 border-red-error text-red-error" />
    <div class="bg-green-success/10 border-green-success text-green-success" />
    <div class="bg-status-warning-yellow/10 border-status-warning-yellow text-status-warning-yellow" />

    <div class="bg-red-error/20 border-red-error text-red-error" />
    <div class="bg-green-success/20 border-green-success text-green-success" />
    <div class="bg-status-warning-yellow/20 border-status-warning-yellow text-status-warning-yellow" />

    <div class="relative overflow-hidden h-full">
      <div
        v-if="!phases_scrolled_to_start"
        class="pointer-events-none absolute w-40 h-[80vh] bg-gradient-to-r dark:from-dark-screen-background from-light-screen-background z-40 -ml-5" />

      <div
        v-if="!phases_scrolled_to_end"
        class="pointer-events-none absolute w-40 h-[80vh] bg-gradient-to-l dark:from-dark-screen-background from-light-screen-background z-40 -right-5" />

      <div
        :class="{
          'no-scrollbar': selected_journey?.phases
            ? !selected_journey?.phases[0]?.records_count && selected_journey?.phases[0]?.records_count !== 0
            : null
        }"
        class="overflow-x-scroll h-full overflow-y-hidden phases-wrapper"
        @scroll.passive="handlePhasesScroll">
        <div class="flex whitespace-nowrap items-stretch w-full ]">
          <draggable
            v-if="selected_journey"
            v-bind="dragOptions"
            key="id"
            v-model="selected_journey.phases"
            handle=".handle"
            filter=".ignore-elements"
            :group="{ name: 'phases', pull: true, put: false, revertClone: false }"
            :component-data="{ tag: 'div', type: 'transition-group' }"
            item-key="id"
            role="list"
            class="flex whitespace-nowrap items-stretch w-1/3"
            @change="phasesOrderChanged(selected_journey)"
            @start="drag = true"
            @end="drag = false">
            <template #item="{ element: phase }">
              <PhaseSummaryCard
                :toggle-selected-all-records="toggleSelectedAllRecords"
                :phase="phase"
                :selected-journey="selected_journey"
                :journeys="journeys" />
            </template>
          </draggable>
        </div>

        <div
          :class="[
            `items-stretch w-full -mt-2 relative z-0  flex whitespace-nowrap pb-5 ${
              record_click_id ? 'pointer-events-none' : ''
            }  dark:bg-dark-screen-background`
          ]">
          <div
            v-for="(phase, i) in selected_journey.phases"
            :key="phase.id"
            :style="{ minWidth: '18vw', maxWidth: '18vw' }"
            :class="[
              `w-full h-full min-w-[23.5%] mr-6  relative pb-4 rounded-b-2xl ${
                !phase.records_count && phase.records_count !== 0
                  ? ''
                  : phase.records_count_change
                    ? phase.records_count_change > 0
                      ? 'bg-green-success/10 '
                      : 'bg-red-error'
                    : 'bg-status-warning-yellow'
              }/10`
            ]">
            <div
              :class="`${
                phase.records_count_change ? (phase.records_count_change > 0 ? 'green-scrollbar ' : 'red-scrollbar') : 'yellow-scrollbar'
              } ${!phase.records_count && phase.records_count !== 0 ? 'no-scrollbar' : ''}`"
              class="mt-6 max-h-[calc(100vh-400px)] overflow-y-auto overflow-x-hidden px-2 green-scrollbar"
              @scroll="(e) => handlePhaseScroll(e, i)">
              <div class="flex overflow-x-hidden">
                <div class="w-full">
                  <div v-for="record in phase?.results ? phase.results : phase.records?.results" :key="record.id">
                    <PhaseRecordCard
                      :scroll-top="scroll_top_values[i]"
                      :i="i"
                      :selected-records="selected_records"
                      :record-click-id="record_click_id"
                      :record-hover-id="record_hover_id"
                      :phases-hover="phases_hover"
                      :handle-record-leave="handleRecordLeave"
                      :handle-lock-click="handleLockClick"
                      :handle-click-outside="handleClickOutside"
                      :handle-check-click="handleCheckClick"
                      :handle-record-enter="handleRecordEnter"
                      :record="record"
                      :selected-journey="selected_journey"
                      :phase="phase" />
                  </div>
                </div>

                <div
                  v-if="phase.scrollbar_height"
                  :style="{
                    opacity: phases_hover ? '0.3' : '1',
                    marginTop: `${phase.scroll_top * 2}px`,
                    height: `${phase.scrollbar_height ? phase.scrollbar_height : '0'}px`
                  }"
                  class="relative left-1.5 bg-[#B3CAD6] rounded-full overflow-hidden right-0 dark:bg-dark-box-background text-[#B3CAD6] dark:text-dark-box-background">
                  b
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="!selected_journey.phases.length"
          class="bg-white dark:bg-dark-box-background flex justify-center min-h-[200px] rounded-lg m-2">
          <div class="flex justify-center items-center flex-col">
            <h3 class="mt-2 text-xl font-normal text-black dark:text-white">
              {{ $t("You currently don't have any phases in this journey") }}
            </h3>
            <p class="mt-2 text-md font-normal text-gray-500">
              {{ $t("Add a new phase to get started!") }}
            </p>
            <div
              class="flex mt-6 font-normal cursor-pointer items-center border-transparent !bg-[#0D8ABA] px-2 text-white dark:bg-dark-brand-blue rounded border-solid border-[1px] w-fit-content mr-[4px] h-[35px] content-center text-[14px]"
              @click="
                () => {
                  $emitter.emit(ALL_EMITTERS.CREATE_NEW_PHASE, { journeys, selected_journey: selectedJourney })
                }
              ">
              <Plus class="w-5 h-5 ml-1" />
              <div class="pl-1">
                <span>{{ $t("Create your first phase") }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dataService from "@/services/data.service"
import { Plus } from "lucide-vue-next"
import draggable from "vuedraggable"

import PhaseSummaryCard from "./PhaseSummaryCard.vue"

import PhaseRecordCard from "./PhaseRecordCard.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    Plus,
    draggable,
    PhaseSummaryCard,
    PhaseRecordCard
  },
  props: {
    selectedJourney: {
      type: Object,
      required: false
    },
    journeys: {
      type: Array,
      required: true
    }
  },
  emits: ["update:selectedJourney"],
  data() {
    return {
      ALL_EMITTERS,
      selected_journey: null,
      selected_records: new Set(),

      phases_scrolled_to_end: false,
      phases_scrolled_to_start: true,
      phases_hover: false,
      record_hover_id: null,
      record_click_id: null,
      drag: false,
      scroll_top_values: [],
      phases_loading: false
    }
  },

  computed: {
    dragOptions() {
      return {
        animation: 200,
        group: "phases",
        disabled: false,
        ghostClass: "ghost",
        chosenClass: "chosen"
      }
    }
  },
  watch: {
    selectedJourney() {
      this.selected_journey = this.selectedJourney
    }
  },

  created() {
    this.selected_journey = this.selectedJourney
  },

  methods: {
    phasesOrderChanged(journey) {
      dataService.updateJourney(journey.id, { phases: journey.phases.map((p) => p.id) })
    },

    handlePhaseScroll(e, i) {
      if (!this.phases_loading) {
        this.scroll_top_values[i] = e.target.scrollTop
        const phases_copy = [...this.selected_journey.phases]
        // phases_copy.splice(i, 1, { ...this.phases_copy[i], scroll_top: e.target.scrollTop, scrollbar_height: e.target.offsetHeight - (e.target.scrollHeight - e.target.offsetHeight) })
        // this.selected_journey = { ...this.selected_journey, phases: phases_copy }
        if (phases_copy[i].records_count > 10 && phases_copy[i].records.results.length < phases_copy[i].records_count) {
          if (e.target.scrollTop + e.target.offsetHeight + 20 >= e.target.scrollHeight) {
            if (phases_copy[i].records.next) {
              this.phases_loading = true
              return dataService.getRecordsByUrl(phases_copy[i].records.next).then((result) => {
                this.phases_loading = false
                phases_copy.splice(i, 1, {
                  ...phases_copy[i],
                  results: [
                    ...(phases_copy[i].results
                      ? phases_copy[i].results.concat(result.data.records.results)
                      : phases_copy[i].records.results.concat(result.data.records.results))
                  ],
                  records: { ...phases_copy[i].records, next: result.data.records.next }
                })
                this.selected_journey = {
                  ...this.selected_journey,
                  phases: [...phases_copy],
                  scroll_top: e.target.scrollTop,
                  scrollbar_height: e.target.offsetHeight - (e.target.scrollHeight - e.target.offsetHeight)
                }
                this.$emit("update:selectedJourney", this.selected_journey)
              })
            }
          }
        }
      }
    },
    handleRecordEnter(phase) {
      if (!this.record_click_id) {
        this.record_hover_id = phase.id
        this.$utils.debounce(() => {
          if (this.record_hover_id) {
            this.phases_hover = true
          }
        }, 800)
      }
    },

    handleCheckClick(e, phase) {
      this.toggleSelectedRecord(phase, e.target.checked)
      this.phases_hover = true
      if (this.record_click_id) {
        this.record_click_id = null
      } else {
        this.record_click_id = phase.id
      }
    },

    handleClickOutside() {
      if (this.record_click_id) {
        this.record_click_id = null
      }
    },
    handleLockClick(record) {
      if (this.record_click_id) {
        this.$utils.debounce(() => {
          this.record_click_id = null
        }, 10)
      } else {
        this.$utils.debounce(() => {
          this.record_click_id = record.id
        }, 10)
      }
    },

    handleRecordLeave() {
      if (!this.record_click_id) {
        this.phases_hover = false
        this.record_hover_id = null
      }
    },
    handlePhasesScroll() {
      let content = document.querySelector(".phases-wrapper")
      const scrolled_to_start = content.scrollLeft <= 0
      const scrolled_to_end = content.scrollWidth - 20 <= content.offsetWidth + content.scrollLeft
      this.phases_scrolled_to_start = scrolled_to_start
      this.phases_scrolled_to_end = scrolled_to_end
    },

    toggleSelectedAllRecords(records, isChecked) {
      if (isChecked) {
        this.selected_records = new Set(records.map((x) => x.id))
      } else {
        this.selected_records = new Set()
      }
    },
    toggleSelectedRecord(record, isChecked) {
      if (isChecked) {
        this.selected_records.add(record.id)
      } else {
        this.selected_records.delete(record.id)
      }
    }
  }
}
</script>
